import React from 'react';
import {
  Box,
  VStack,
  Text,
  RadioGroup,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { MathRenderer } from '@/common/components/MathRenderer';
import type { MCQQuestion } from './types';
import { OptionButton } from './OptionButton';
import { AspectImage } from './AspectImage';

interface QuestionCardProps {
  question: MCQQuestion;
  questionNumber: number;
  selectedAnswer?: string;
  onSelectAnswer: (optionId: string) => void;
  showResults?: boolean;
  correctAnswerId?: string;
  feedback?: string;
  isDisabled?: boolean;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  questionNumber,
  selectedAnswer,
  onSelectAnswer,
  showResults = false,
  correctAnswerId,
  feedback,
  isDisabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <Box bg="$bgCard" borderRadius="$lg" p="$4" mb="$4" borderWidth={1} borderColor="$borderLight">
      <VStack space="md">
        {/* Question Header */}
        <Box>
          <Text size="sm" color="$textSubtle" mb="$1">
            {t('student.mcq.questionNumber', { number: questionNumber })}
          </Text>

          <MathRenderer style={{ fontSize: 16, fontWeight: '600' }}>
            {question.text}
          </MathRenderer>

          {!!question.imageUrl && (
            <Box mt="$3">
              <AspectImage
                uri={question.imageUrl}
                alt={question.imageAlt || 'Question image'}
                maxHeight={320}
              />
            </Box>
          )}
        </Box>

        {/* Options */}
        <RadioGroup value={selectedAnswer} onChange={onSelectAnswer}>
          {question.options.map((option) => (
            <OptionButton
              key={option.id}
              option={option}
              questionId={question.id}
              isSelected={selectedAnswer === option.id}
              onSelect={() => onSelectAnswer(option.id)}
              showCorrect={showResults}
              isCorrect={option.id === correctAnswerId}
              isDisabled={isDisabled}
            />
          ))}
        </RadioGroup>

        {/* Feedback (shown after submission) */}
        {!!showResults && !!feedback && (
          <Box bg="$info50" borderLeftWidth={4} borderLeftColor="$info500" p="$3" borderRadius="$sm">
            <Text size="sm" color="$info700">{feedback}</Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default QuestionCard;
